/**
 * Session token storage strategies.
 *
 * Security rationale for NOT using localStorage:
 * - localStorage is accessible by any JavaScript running on the page (XSS).
 * - An XSS vulnerability would allow an attacker to read the token and make
 *   arbitrary authenticated API requests from any origin.
 * - Storing in memory means the token is lost on page refresh (acceptable for
 *   a 1-hour session token with no refresh endpoint) and is never written to
 *   any persistent browser storage.
 * - The secure-cookie strategy relies on the browser enforcing HttpOnly +
 *   SameSite=Strict, which prevents JavaScript from reading the token and
 *   protects against CSRF. Use this strategy for SSR/cookie-based setups.
 *
 * Token is treated as opaque (no decode for security decisions). The optional
 * `exp` parsing is provided as a convenience for pre-emptive UX hints only —
 * the backend is the authoritative expiry check.
 */

export interface SessionStore {
  /** Returns the current token, or null if unauthenticated. */
  getToken(): string | null;
  /** Stores a new token after successful login. */
  setToken(token: string): void;
  /** Clears the stored token (logout, 401, etc.). */
  clear(): void;
}

/**
 * In-memory session store. Default strategy.
 *
 * Token lives only in the JS heap; it is never written to localStorage,
 * sessionStorage, or any persistent browser API. Cleared on page reload —
 * the user re-authenticates, which is acceptable for a 1-hour session with no
 * refresh endpoint.
 */
export function createMemorySessionStore(): SessionStore {
  let token: string | null = null;
  return {
    getToken() {
      return token;
    },
    setToken(t) {
      token = t;
    },
    clear() {
      token = null;
    },
  };
}

/**
 * Browser-storage-backed session store. Persists the token across page reloads.
 *
 * SECURITY TRADE-OFF (deliberate): unlike the in-memory store, the token is
 * written to a browser Storage (sessionStorage by default), so it is readable
 * by any JavaScript on the page and is therefore exposed to XSS. We accept this
 * to keep the user signed in across a refresh — a hard product requirement —
 * and mitigate it by using `sessionStorage` (cleared when the tab closes, never
 * shared across tabs/windows) rather than `localStorage`, and by keeping the
 * session token short-lived (≈1 hour) with no refresh endpoint. The backend
 * remains the authoritative validator of every token.
 *
 * All Storage access is wrapped in try/catch: a disabled/quota-exhausted/
 * private-mode Storage degrades gracefully to "no persisted token" rather than
 * throwing during render.
 */
export function createWebStorageSessionStore(
  storage: Storage,
  key = 'hostesa.session.token',
): SessionStore {
  return {
    getToken() {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    setToken(t) {
      try {
        storage.setItem(key, t);
      } catch {
        // Storage unavailable (private mode, quota) — session stays in-memory only.
      }
    },
    clear() {
      try {
        storage.removeItem(key);
      } catch {
        // Nothing to clear if storage is unavailable.
      }
    },
  };
}

/**
 * Parses the expiry (`exp`) claim from a JWT without verifying the signature.
 *
 * SECURITY NOTE: this is decode-only — it does NOT validate the signature, the
 * audience, or the issuer. Never use this result to make security decisions.
 * Its sole purpose is to allow the UI to show a "session expiring soon" hint or
 * to proactively redirect to login before a request is sent.
 *
 * Returns the `exp` Unix timestamp in milliseconds, or null if the token does
 * not contain a parseable `exp` claim.
 */
export function parseTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    // Base64url → base64 → JSON
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed === 'object' && parsed !== null && 'exp' in parsed) {
      const exp = (parsed as Record<string, unknown>)['exp'];
      if (typeof exp === 'number') {
        return exp * 1000; // convert seconds to milliseconds
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Returns true when the token's `exp` claim is in the past.
 *
 * SECURITY NOTE: decode-only, no signature verification. Use as a UX hint
 * only — the backend is authoritative. A missing `exp` is treated as
 * non-expired (unknown) to avoid false positives.
 */
export function isTokenExpired(token: string): boolean {
  const expMs = parseTokenExpiry(token);
  if (expMs === null) return false;
  return Date.now() >= expMs;
}

/**
 * Secure-cookie strategy documentation (not implemented here — requires SSR or
 * a cookie-setting endpoint on the same origin).
 *
 * To implement:
 * 1. After login, have the server (or a Next.js/TanStack Start route handler)
 *    set a `Set-Cookie: session=<token>; HttpOnly; Secure; SameSite=Strict`
 *    header instead of returning the token in the response body.
 * 2. Implement `getToken()` to issue a lightweight `/api/v1/auth/me` call and
 *    rely on the browser sending the cookie automatically — never read it
 *    from `document.cookie`.
 * 3. Implement `clear()` to call a `POST /api/v1/auth/logout` endpoint that
 *    sets `Set-Cookie: session=; Max-Age=0`.
 *
 * This strategy provides stronger XSS protection but requires backend support
 * for cookie-based auth. The in-memory strategy above is used by default until
 * then.
 */
export const SECURE_COOKIE_STRATEGY_NOTE = 'see session.ts for implementation guidance';
